import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CreateButton from './CreateButton';
import PageNavigator from './PageNavigator';
import styles from './FinanceTabContent.module.css';
import { fetchPaidInstallments, fetchUnpaidInstallments, closeInstallment, deleteInstallment, openInstallment, exportInstallments } from '../services/installmentService';
import { fetchPaidMaintenance, fetchUnpaidMaintenance, closeMaintenance, deleteMaintenance, openMaintenance, exportMaintenance } from '../services/maintenanceService';
import { fetchPaidUtility, fetchUnpaidUtility, closeUtility, deleteUtility, openUtility, exportUtility } from '../services/utilityService';
import { useRef } from 'react';
import FilterDialog from './FilterDialog';
import { searchProjects } from '../services/projectService';
import { searchCustomers } from '../services/customerService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import { useCachedPermissions } from '../hooks/useCachedPermissions';

const ITEMS_PER_PAGE = 20;

const FinanceTabContent = ({ selectedTab }) => {
    const dispatch = useDispatch();

    // Get finance permissions
    const { hasDeleteFinance, hasCloseFinance, hasExportFinance } = useCachedPermissions();
    const [financeData, setFinanceData] = useState({ paid: [], unpaid: [] });
    const [maintenanceData, setMaintenanceData] = useState({ paid: [], unpaid: [] });
    const [utilityData, setUtilityData] = useState({ paid: [], unpaid: [] });
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const actionMenuRef = useRef(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [projectSearchResults, setProjectSearchResults] = useState([]);
    const [projectSearchLoading, setProjectSearchLoading] = useState(false);
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [filters, setFilters] = useState({
        project: null,
        date: '',
        customer: null
    });
    const [paginationData, setPaginationData] = useState({
        total: 0,
        page: 1,
        limit: ITEMS_PER_PAGE,
        results: 0,
        last_page: 1
    });

    // Add debug effect to track filter state changes
    useEffect(() => {
        console.log('Filters state changed:', filters);
    }, [filters]);

    // Project search function
    const searchProjectsFunction = async (searchTerm) => {
        // Don't search if term is empty or too short
        if (!searchTerm || searchTerm.trim().length < 2) {
            setProjectSearchResults([]);
            setProjectSearchLoading(false);
            return;
        }

        setProjectSearchLoading(true);
        try {
            const response = await searchProjects(searchTerm.trim());
            console.log('Project search response:', response);
            console.log('Project search response type:', typeof response);
            console.log('Project search response keys:', Object.keys(response || {}));

            // Handle different response structures
            if (response.data && Array.isArray(response.data)) {
                console.log('Using response.data for projects:', response.data);
                setProjectSearchResults(response.data);
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                console.log('Using response.data.data for projects:', response.data.data);
                setProjectSearchResults(response.data.data);
            } else if (Array.isArray(response)) {
                console.log('Using response directly for projects:', response);
                setProjectSearchResults(response);
            } else {
                console.log('No valid array found in project response, setting empty array');
                setProjectSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching projects:', error);
            setProjectSearchResults([]);
        } finally {
            setProjectSearchLoading(false);
        }
    };

    // Customer search function
    const searchCustomersFunction = async (searchTerm) => {
        // Don't search if term is empty or too short
        if (!searchTerm || searchTerm.trim().length < 2) {
            setCustomerSearchResults([]);
            setCustomerSearchLoading(false);
            return;
        }

        setCustomerSearchLoading(true);
        try {
            // Note: The searchCustomers API expects a swag_id parameter
            // We're searching by SWAG ID
            const response = await searchCustomers(searchTerm.trim());
            console.log('Customer search response:', response);
            console.log('Customer search response type:', typeof response);
            console.log('Customer search response keys:', Object.keys(response || {}));

            // Handle different response structures
            let customers = [];
            if (response.data && Array.isArray(response.data)) {
                console.log('Using response.data:', response.data);
                customers = response.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                console.log('Using response.data.data:', response.data.data);
                customers = response.data.data;
            } else if (Array.isArray(response)) {
                console.log('Using response directly:', response);
                customers = response;
            } else {
                console.log('No valid array found in response, setting empty array');
                customers = [];
            }

            // Log the structure of the first customer if available
            if (customers.length > 0) {
                console.log('First customer object:', customers[0]);
                console.log('First customer object keys:', Object.keys(customers[0]));
            }

            setCustomerSearchResults(customers);
        } catch (error) {
            console.error('Error searching customers:', error);
            // If email-based search fails, we could try a different approach
            // For now, just set empty results
            setCustomerSearchResults([]);
        } finally {
            setCustomerSearchLoading(false);
        }
    };

    // Helper function for debouncing
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Create debounced search functions
    const debouncedProjectSearch = debounce(searchProjectsFunction, 300);
    const debouncedCustomerSearch = debounce(searchCustomersFunction, 300);

    // Define filter fields
    const filterFields = [
        {
            type: 'searchable-select',
            name: 'project',
            label: 'Project',
            placeholder: 'Search for a project...',
            onSearch: debouncedProjectSearch,
            searchOptions: projectSearchResults,
            loading: projectSearchLoading,
            displayKey: 'name',
            valueKey: '_id'
        },
        {
            type: 'date',
            name: 'date',
            label: 'Date',
            placeholder: 'Select date'
        },
        {
            type: 'searchable-select',
            name: 'customer',
            label: 'Customer',
            placeholder: 'Search by SWAG ID...',
            onSearch: debouncedCustomerSearch,
            searchOptions: customerSearchResults,
            loading: customerSearchLoading,
            displayKey: 'name', // Try 'name' first, might fallback to 'email' or other fields
            valueKey: '_id' // Try '_id' first, might fallback to 'id' 
        }
    ];

    // Fetch data with filters (installments, maintenance, utility)
    const fetchFinanceData = async (selectedTab, page, filters) => {
        console.log('fetchFinanceData called with:', { selectedTab, page, filters });

        setLoading(true);
        setError(null);

        try {
            let result;

            console.log('=== FILTER PROCESSING START ===');
            console.log('Raw filters object:', filters);
            console.log('filters.project type:', typeof filters.project);
            console.log('filters.date type:', typeof filters.date);
            console.log('filters.customer type:', typeof filters.customer);

            // Extract IDs more safely with multiple fallback options
            const projectId = filters.project ?
                (filters.project._id || filters.project.id || filters.project.projectId) : null;
            const customerId = filters.customer ?
                (filters.customer._id || filters.customer.id || filters.customer.customerId || filters.customer.userId) : null;

            const filterParams = {
                ...(filters.date && { date: filters.date }),
                ...(projectId && { projectId: projectId }),
                ...(customerId && { customerId: customerId })
            };
            if (filters.project) {
                console.log('filters.project keys:', Object.keys(filters.project));
            }
            if (filters.customer) {
                console.log('filters.customer keys:', Object.keys(filters.customer));
            }

            console.log('Constructed filter params:', filterParams);
            console.log('=== FILTER PROCESSING END ===');

            // Installment tabs
            if (selectedTab === 'finance-paid') {
                console.log('Calling fetchPaidInstallments with:', page, ITEMS_PER_PAGE, filterParams);
                result = await fetchPaidInstallments(page, ITEMS_PER_PAGE, filterParams);
                const paidData = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
                console.log(paidData);
                setFinanceData(prev => ({ ...prev, paid: paidData }));
            } else if (selectedTab === 'finance-unpaid') {
                console.log('Calling fetchUnpaidInstallments with:', page, ITEMS_PER_PAGE, filterParams);
                result = await fetchUnpaidInstallments(page, ITEMS_PER_PAGE, filterParams);
                const unpaidData = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
                setFinanceData(prev => ({ ...prev, unpaid: unpaidData }));
            }
            // Maintenance tabs
            else if (selectedTab === 'maintenance-paid') {
                console.log('Calling fetchPaidMaintenance with:', page, ITEMS_PER_PAGE, filterParams);
                result = await fetchPaidMaintenance(page, ITEMS_PER_PAGE, filterParams);
                const paidData = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
                setMaintenanceData(prev => ({ ...prev, paid: paidData }));
            } else if (selectedTab === 'maintenance-unpaid') {
                console.log('Calling fetchUnpaidMaintenance with:', page, ITEMS_PER_PAGE, filterParams);
                result = await fetchUnpaidMaintenance(page, ITEMS_PER_PAGE, filterParams);
                const unpaidData = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
                setMaintenanceData(prev => ({ ...prev, unpaid: unpaidData }));
            }
            // Utility tabs
            else if (selectedTab === 'utility-paid') {
                console.log('Calling fetchPaidUtility with:', page, ITEMS_PER_PAGE, filterParams);
                result = await fetchPaidUtility(page, ITEMS_PER_PAGE, filterParams);
                const paidData = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
                setUtilityData(prev => ({ ...prev, paid: paidData }));
            } else if (selectedTab === 'utility-unpaid') {
                console.log('Calling fetchUnpaidUtility with:', page, ITEMS_PER_PAGE, filterParams);
                result = await fetchUnpaidUtility(page, ITEMS_PER_PAGE, filterParams);
                const unpaidData = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
                setUtilityData(prev => ({ ...prev, unpaid: unpaidData }));
            }

            // Store pagination metadata for all tabs
            if (result && result.data) {
                const newPaginationData = {
                    total: result.data.total || 0,
                    page: result.data.page || 1,
                    limit: result.data.limit || ITEMS_PER_PAGE,
                    results: result.data.results || 0,
                    last_page: result.data.last_page || 1
                };
                console.log('Updated pagination data:', newPaginationData);
                setPaginationData(newPaginationData);
            }
        } catch (err) {
            console.error('Error in fetchFinanceData:', err);
            const tabType = selectedTab.includes('finance') ? 'installment' : 
                          selectedTab.includes('maintenance') ? 'maintenance' : 'utility';
            const status = selectedTab.includes('paid') ? 'paid' : 'unpaid';
            setError(err.message || `Failed to fetch ${status} ${tabType} records`);
            
            // Clear data based on tab type
            if (selectedTab.includes('finance')) {
                setFinanceData(prev => ({ 
                    ...prev, 
                    [status]: [] 
                }));
            } else if (selectedTab.includes('maintenance')) {
                setMaintenanceData(prev => ({ 
                    ...prev, 
                    [status]: [] 
                }));
            } else if (selectedTab.includes('utility')) {
                setUtilityData(prev => ({ 
                    ...prev, 
                    [status]: [] 
                }));
            }
            
            // Reset pagination data on error
            setPaginationData({
                total: 0,
                page: 1,
                limit: ITEMS_PER_PAGE,
                results: 0,
                last_page: 1
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('Filters state changed:', filters);
        console.log('Filters state keys:', Object.keys(filters));
        console.log('Current selectedTab:', selectedTab);
        console.log('Current currentPage:', currentPage);

        const validTabs = [
            'finance-paid', 'finance-unpaid',
            'maintenance-paid', 'maintenance-unpaid',
            'utility-paid', 'utility-unpaid'
        ];

        if (validTabs.includes(selectedTab)) {
            // Clear data immediately when switching tabs to prevent flicker
            if (selectedTab.includes('finance')) {
                const status = selectedTab.includes('paid') ? 'paid' : 'unpaid';
                setFinanceData(prev => ({ ...prev, [status]: [] }));
            } else if (selectedTab.includes('maintenance')) {
                const status = selectedTab.includes('paid') ? 'paid' : 'unpaid';
                setMaintenanceData(prev => ({ ...prev, [status]: [] }));
            } else if (selectedTab.includes('utility')) {
                const status = selectedTab.includes('paid') ? 'paid' : 'unpaid';
                setUtilityData(prev => ({ ...prev, [status]: [] }));
            }

            // Reset pagination data when switching tabs
            setPaginationData({
                total: 0,
                page: 1,
                limit: ITEMS_PER_PAGE,
                results: 0,
                last_page: 1
            });

            fetchFinanceData(selectedTab, currentPage, filters);
        }
    }, [selectedTab, currentPage, filters]);

    // Dropdown logic
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdownId) {
                // Check if the clicked element is inside any dropdown menu or action button
                const isDropdownMenuClick = event.target.closest('[class*="dropdownMenu"]');
                const isActionButtonClick = event.target.closest('[class*="actionMenuButton"]');
                const isDropdownItemClick = event.target.closest('[class*="dropdownItem"]');

                if (!isDropdownMenuClick && !isActionButtonClick && !isDropdownItemClick) {
                    setOpenDropdownId(null);
                }
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && openDropdownId) {
                setOpenDropdownId(null);
            }
        };

        if (openDropdownId) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [openDropdownId]);

    const handleDropdownToggle = (e, itemId) => {
        e.preventDefault();
        e.stopPropagation();

        if (openDropdownId === itemId) {
            setOpenDropdownId(null);
            return;
        }

        setOpenDropdownId(itemId);
    };

    // Generic delete handler for all types
    const handleDelete = async (itemId, type) => {
        if (!hasDeleteFinance) {
            dispatch(showErrorNotification('You do not have permission to delete finance records'));
            return;
        }

        if (!itemId) {
            dispatch(showErrorNotification('Invalid item ID'));
            return;
        }

        setActionLoadingId(itemId);
        try {
            let deleteFunction, updateStateFunction, successMessage;
            
            if (type === 'installment') {
                deleteFunction = deleteInstallment;
                updateStateFunction = (status) => setFinanceData(prev => ({
                    ...prev,
                    [status]: prev[status].filter(item => item.id !== itemId)
                }));
                successMessage = 'Installment deleted successfully!';
            } else if (type === 'maintenance') {
                deleteFunction = deleteMaintenance;
                updateStateFunction = (status) => setMaintenanceData(prev => ({
                    ...prev,
                    [status]: prev[status].filter(item => item.id !== itemId)
                }));
                successMessage = 'Maintenance deleted successfully!';
            } else if (type === 'utility') {
                deleteFunction = deleteUtility;
                updateStateFunction = (status) => setUtilityData(prev => ({
                    ...prev,
                    [status]: prev[status].filter(item => item.id !== itemId)
                }));
                successMessage = 'Utility deleted successfully!';
            }

            await deleteFunction(itemId);
            
            // Determine status from selectedTab
            const status = selectedTab.includes('paid') ? 'paid' : 'unpaid';
            updateStateFunction(status);
            
            setOpenDropdownId(null);
            dispatch(showSuccessNotification(successMessage));
        } catch (err) {
            console.error('Delete error:', err);
            dispatch(showErrorNotification(err.message || `Failed to delete ${type}`));
        } finally {
            setActionLoadingId(null);
        }
    };

    // Generic close handler for all types
    const handleClose = async (itemId, type) => {
        if (!hasCloseFinance) {
            dispatch(showErrorNotification('You do not have permission to close finance records'));
            return;
        }

        if (!itemId) {
            dispatch(showErrorNotification('Invalid item ID'));
            return;
        }

        setActionLoadingId(itemId);
        try {
            let closeFunction, updateStateFunction, successMessage;
            
            if (type === 'installment') {
                closeFunction = closeInstallment;
                updateStateFunction = () => setFinanceData(prev => ({
                    ...prev,
                    unpaid: prev.unpaid.filter(item => item.id !== itemId)
                }));
                successMessage = 'Installment closed successfully!';
            } else if (type === 'maintenance') {
                closeFunction = closeMaintenance;
                updateStateFunction = () => setMaintenanceData(prev => ({
                    ...prev,
                    unpaid: prev.unpaid.filter(item => item.id !== itemId)
                }));
                successMessage = 'Maintenance closed successfully!';
            } else if (type === 'utility') {
                closeFunction = closeUtility;
                updateStateFunction = () => setUtilityData(prev => ({
                    ...prev,
                    unpaid: prev.unpaid.filter(item => item.id !== itemId)
                }));
                successMessage = 'Utility closed successfully!';
            }

            await closeFunction(itemId);
            updateStateFunction();
            setOpenDropdownId(null);
            dispatch(showSuccessNotification(successMessage));
        } catch (err) {
            console.error('Close error:', err);
            dispatch(showErrorNotification(err.message || `Failed to close ${type}`));
        } finally {
            setActionLoadingId(null);
        }
    };

    // Generic open handler for all types
    const handleOpen = async (itemId, type) => {
        if (!itemId) {
            dispatch(showErrorNotification('Invalid item ID'));
            return;
        }

        setActionLoadingId(itemId);
        try {
            let openFunction, updateStateFunction, successMessage;
            
            if (type === 'installment') {
                openFunction = openInstallment;
                updateStateFunction = () => setFinanceData(prev => ({
                    ...prev,
                    paid: prev.paid.filter(item => item.id !== itemId)
                }));
                successMessage = 'Installment reopened successfully!';
            } else if (type === 'maintenance') {
                openFunction = openMaintenance;
                updateStateFunction = () => setMaintenanceData(prev => ({
                    ...prev,
                    paid: prev.paid.filter(item => item.id !== itemId)
                }));
                successMessage = 'Maintenance reopened successfully!';
            } else if (type === 'utility') {
                openFunction = openUtility;
                updateStateFunction = () => setUtilityData(prev => ({
                    ...prev,
                    paid: prev.paid.filter(item => item.id !== itemId)
                }));
                successMessage = 'Utility reopened successfully!';
            }

            await openFunction(itemId);
            updateStateFunction();
            setOpenDropdownId(null);
            dispatch(showSuccessNotification(successMessage));
        } catch (err) {
            console.error('Open error:', err);
            dispatch(showErrorNotification(err.message || `Failed to reopen ${type}`));
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleAddFinance = () => {
        console.log('Create Payment clicked');
        // Here you would typically navigate to add finance form
    };

    const handleFilter = () => {
        setIsFilterDialogOpen(true);
    };

    const handleApplyFilter = (filterValues) => {
        console.log('Apply filter called with values:', filterValues);
        console.log('Apply filter - filterValues type:', typeof filterValues);
        console.log('Apply filter - filterValues keys:', Object.keys(filterValues || {}));

        // Log each individual filter value
        if (filterValues) {
            console.log('filterValues.project:', filterValues.project);
            console.log('filterValues.date:', filterValues.date);
            console.log('filterValues.customer:', filterValues.customer);
        }

        console.log('Current filters state before update:', filters);
        setFilters(filterValues);
        console.log('Called setFilters with:', filterValues);
        setCurrentPage(1); // Reset to first page when applying filters
        setIsFilterDialogOpen(false);
    };

    const handleFilterClose = () => {
        setIsFilterDialogOpen(false);
        // Clear search results when closing dialog
        setProjectSearchResults([]);
        setCustomerSearchResults([]);
    };

    const handleResetFilter = () => {
        setFilters({
            project: null,
            date: '',
            customer: null
        });
        setCurrentPage(1);
    };

    const handleExport = async () => {
        if (!hasExportFinance) {
            dispatch(showErrorNotification('You do not have permission to export finance records'));
            return;
        }
        setExportLoading(true);
        
        let status = 'paid';
        let exportFunction, type;
        
        // Determine status
        if (selectedTab.includes('unpaid')) status = 'unpaid';
        else if (selectedTab.includes('paid')) status = 'paid';
        
        // Determine type and export function
        if (selectedTab.includes('finance')) {
            exportFunction = exportInstallments;
            type = 'installments';
        } else if (selectedTab.includes('maintenance')) {
            exportFunction = exportMaintenance;
            type = 'maintenance';
        } else if (selectedTab.includes('utility')) {
            exportFunction = exportUtility;
            type = 'utility';
        }
        
        try {
            await exportFunction(status);
            dispatch(showSuccessNotification(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully!`));
        } catch (err) {
            dispatch(showErrorNotification(err.message || `Failed to export ${type}`));
        } finally {
            setExportLoading(false);
        }
    };

    // Safe action handlers that prevent event bubbling
    const handleDropdownItemClick = (e, action, itemId) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            // Determine the type based on current tab
            let type;
            if (selectedTab.includes('finance')) type = 'installment';
            else if (selectedTab.includes('maintenance')) type = 'maintenance';
            else if (selectedTab.includes('utility')) type = 'utility';

            switch (action) {
                case 'deletePaid':
                case 'deleteUnpaid':
                    handleDelete(itemId, type);
                    break;
                case 'openPaid':
                    handleOpen(itemId, type);
                    break;
                case 'closeUnpaid':
                    handleClose(itemId, type);
                    break;
                default:
                    console.warn('Unknown action:', action);
            }
        } catch (err) {
            console.error('Action handler error:', err);
            dispatch(showErrorNotification('An error occurred while performing the action'));
        }
    };



    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusText = () => {
        if (selectedTab.includes('unpaid')) return 'Unpaid';
        if (selectedTab.includes('paid')) return 'Paid';
        return 'All';
    };

    const getFilteredData = () => {
        if (selectedTab === 'finance') return [];
        if (selectedTab === 'maintenance') return [];
        if (selectedTab === 'utility') return [];
        
        if (selectedTab.includes('finance')) {
            if (selectedTab === 'finance-paid') return financeData.paid || [];
            if (selectedTab === 'finance-unpaid') return financeData.unpaid || [];
        } else if (selectedTab.includes('maintenance')) {
            if (selectedTab === 'maintenance-paid') return maintenanceData.paid || [];
            if (selectedTab === 'maintenance-unpaid') return maintenanceData.unpaid || [];
        } else if (selectedTab.includes('utility')) {
            if (selectedTab === 'utility-paid') return utilityData.paid || [];
            if (selectedTab === 'utility-unpaid') return utilityData.unpaid || [];
        }
        
        return [];
    };

    const filteredData = getFilteredData();
    // Use pagination data from API response instead of calculating locally
    const totalPages = paginationData.last_page;
    // Don't slice data since API already handles pagination
    const paginatedData = filteredData;

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([]);
        } else {
            setSelectedItems(paginatedData.map(item => item.id));
        }
        setSelectAll(!selectAll);
    };

    const handleSelectItem = (itemId) => {
        if (selectedItems.includes(itemId)) {
            setSelectedItems(selectedItems.filter(id => id !== itemId));
            setSelectAll(false);
        } else {
            setSelectedItems([...selectedItems, itemId]);
            if (selectedItems.length + 1 === paginatedData.length) {
                setSelectAll(true);
            }
        }
    };

    const handleRowAction = (action, itemId) => {
        console.log(`${action} clicked for item:`, itemId);
        // Here you would implement row actions
    };

    return (
        <div className={styles.financeContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.headerTitle}>
                        {selectedTab.includes('finance') ? 'Installment' :
                         selectedTab.includes('maintenance') ? 'Maintenance' :
                         selectedTab.includes('utility') ? 'Utility' : 'Finance'}
                    </h2>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.actionButton} onClick={handleFilter}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
                        </svg>
                        Filter
                    </button>
                    {(filters.project || filters.date || filters.customer) && (
                        <button className={styles.actionButton} onClick={handleResetFilter}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23,4 23,10 17,10" />
                                <path d="M20.49,15a9,9 0 1,1 -2.12,-9.36L23,10" />
                            </svg>
                            Reset Filter
                        </button>
                    )}
                    {hasExportFinance && (
                        <button className={styles.actionButton} onClick={handleExport} disabled={exportLoading}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7,10 12,15 17,10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            {exportLoading ? 'Exporting...' : 'Export'}
                        </button>
                    )}
                    {/* <CreateButton label="Create Payment" onClick={handleAddFinance} /> */}
                </div>
            </div>

            {loading && (selectedTab.includes('-paid') || selectedTab.includes('-unpaid')) ? (
                <div className={styles.selectSubTabMessage}>
                    <h3>Loading {getStatusText()} {
                        selectedTab.includes('finance') ? 'Installments' :
                        selectedTab.includes('maintenance') ? 'Maintenance' : 'Utility'
                    }...</h3>
                </div>
            ) : error && (selectedTab.includes('-paid') || selectedTab.includes('-unpaid')) ? (
                <div className={styles.selectSubTabMessage}>
                    <h3>Error</h3>
                    <p>{error}</p>
                </div>
            ) : (selectedTab === 'finance' || selectedTab === 'maintenance' || selectedTab === 'utility') ? (
                <div className={styles.selectSubTabMessage}>
                    <h3>Select {
                        selectedTab === 'finance' ? 'Finance' :
                        selectedTab === 'maintenance' ? 'Maintenance' : 'Utility'
                    } Type</h3>
                    <p>Please select "Paid" or "Unpaid" to view records.</p>
                </div>
            ) : filteredData.length > 0 ? (
                <>
                    <div className={styles.tableContainer}>
                        <div className={styles.tableWrapper}>
                            <table className={styles.financeTable}>
                                <thead>
                                    <tr>
                                        <th className={styles.amountColumn}>Amount</th>
                                        {(selectedTab.includes('-paid') || selectedTab.includes('-unpaid')) && (
                                            <th className={styles.statusColumn}>Status</th>
                                        )}
                                        {/* Only show Description column for base tabs (not API) */}
                                        {(selectedTab === 'finance' || selectedTab === 'maintenance' || selectedTab === 'utility') && 
                                            <th className={styles.descriptionColumn}>Description</th>
                                        }
                                        {(selectedTab.includes('-paid') || selectedTab.includes('-unpaid')) && (
                                            <th className={styles.unitColumn}>Unit</th>
                                        )}
                                        <th className={styles.customerColumn}>Customer</th>
                                        <th className={styles.dateColumn}>
                                            {selectedTab.includes('-paid') ? 'Date' :
                                             selectedTab.includes('-unpaid') ? 'Due Date' : 'Date'}
                                        </th>
                                        <th className={styles.actionsColumn}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedData.map((item, index) => (
                                        <React.Fragment key={item.id}>
                                            <tr className={`${styles.tableRow} ${openDropdownId === item.id ? styles.activeDropdown : ''}`}>
                                                <td className={styles.amountColumn}>
                                                    <span className={styles.amountValue}>{item.amount}</span>
                                                    {item.payment_method === 'cash' && (
                                                        <span className={styles.cashBadge}>Cash</span>
                                                    )}
                                                </td>
                                                {(selectedTab.includes('-paid') || selectedTab.includes('-unpaid')) && (
                                                    <td className={styles.statusColumn}>
                                                        <span className={`${styles.statusBadge} ${styles[item.status]}`}>
                                                            {item.status === 'paid' ? 'Paid' : 'Unpaid'}
                                                        </span>
                                                    </td>
                                                )}
                                                {/* Only show Description cell for base tabs (not API) */}
                                                {(selectedTab === 'finance' || selectedTab === 'maintenance' || selectedTab === 'utility') && (
                                                    <td className={styles.descriptionColumn}>
                                                        <span className={styles.descriptionText}>{item.description}</span>
                                                    </td>
                                                )}
                                                {(selectedTab.includes('-paid') || selectedTab.includes('-unpaid')) && (
                                                    <td className={styles.unitColumn}>
                                                        <span>{item.unit_label || '-'}</span>
                                                    </td>
                                                )}
                                                <td className={styles.customerColumn}>
                                                    <span className={styles.customerName}>{item.customer_name}</span>
                                                </td>
                                                <td className={styles.dateColumn}>
                                                    {item.date}
                                                </td>
                                                <td className={styles.actionsColumn}>
                                                    <div className={styles.dropdownContainer}>
                                                        {/* Paid Tab Actions */}
                                                        {selectedTab.includes('-paid') && hasDeleteFinance && (
                                                            <div className={styles.actionWrapper}>
                                                                <button
                                                                    className={styles.actionMenuButton}
                                                                    onClick={(e) => handleDropdownToggle(e, item.id)}
                                                                    title="Actions"
                                                                    disabled={actionLoadingId === item.id}
                                                                    type="button"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <circle cx="12" cy="12" r="1" />
                                                                        <circle cx="19" cy="12" r="1" />
                                                                        <circle cx="5" cy="12" r="1" />
                                                                    </svg>
                                                                </button>
                                                                {openDropdownId === item.id && (
                                                                    <div
                                                                        className={styles.dropdownMenu}
                                                                        ref={actionMenuRef}
                                                                    >
                                                                        <button
                                                                            className={styles.dropdownItem}
                                                                            onClick={(e) => handleDropdownItemClick(e, 'openPaid', item.id)}
                                                                            disabled={actionLoadingId === item.id}
                                                                            type="button"
                                                                        >
                                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                                <circle cx="12" cy="12" r="3" />
                                                                            </svg>
                                                                            {actionLoadingId === item.id ? 'Opening...' : 'Make Not Paid'}
                                                                        </button>
                                                                        <button
                                                                            className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                                                            onClick={(e) => handleDropdownItemClick(e, 'deletePaid', item.id)}
                                                                            disabled={actionLoadingId === item.id}
                                                                            type="button"
                                                                        >
                                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                <polyline points="3,6 5,6 21,6" />
                                                                                <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                                                <line x1="14" y1="11" x2="14" y2="17" />
                                                                            </svg>
                                                                            {actionLoadingId === item.id ? 'Deleting...' : 'Delete'}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Unpaid Tab Actions */}
                                                        {selectedTab.includes('-unpaid') && (hasCloseFinance || hasDeleteFinance) && (
                                                            <div className={styles.actionWrapper}>
                                                                <button
                                                                    className={styles.actionMenuButton}
                                                                    onClick={(e) => handleDropdownToggle(e, item.id)}
                                                                    title="Actions"
                                                                    disabled={actionLoadingId === item.id}
                                                                    type="button"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <circle cx="12" cy="12" r="1" />
                                                                        <circle cx="19" cy="12" r="1" />
                                                                        <circle cx="5" cy="12" r="1" />
                                                                    </svg>
                                                                </button>
                                                                {openDropdownId === item.id && (
                                                                    <div
                                                                        className={styles.dropdownMenu}
                                                                        ref={actionMenuRef}
                                                                    >
                                                                        {hasCloseFinance && (
                                                                            <button
                                                                                className={styles.dropdownItem}
                                                                                onClick={(e) => handleDropdownItemClick(e, 'closeUnpaid', item.id)}
                                                                                disabled={actionLoadingId === item.id}
                                                                                type="button"
                                                                            >
                                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                    <path d="M9 12l2 2 4-4" />
                                                                                </svg>
                                                                                {actionLoadingId === item.id ? 'Closing...' : 'Close'}
                                                                            </button>
                                                                        )}
                                                                        {hasDeleteFinance && (
                                                                            <button
                                                                                className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                                                                onClick={(e) => handleDropdownItemClick(e, 'deleteUnpaid', item.id)}
                                                                                disabled={actionLoadingId === item.id}
                                                                                type="button"
                                                                            >
                                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                    <polyline points="3,6 5,6 21,6" />
                                                                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                                                </svg>
                                                                                {actionLoadingId === item.id ? 'Deleting...' : 'Delete'}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* General Tab Actions */}
                                                        {(selectedTab === 'finance' || selectedTab === 'maintenance' || selectedTab === 'utility') && (hasCloseFinance || hasDeleteFinance) && (
                                                            <div className={styles.actionWrapper}>
                                                                <button
                                                                    className={styles.actionMenuButton}
                                                                    onClick={(e) => handleDropdownToggle(e, item.id)}
                                                                    title="Actions"
                                                                    disabled={actionLoadingId === item.id}
                                                                    type="button"
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <circle cx="12" cy="12" r="1" />
                                                                        <circle cx="19" cy="12" r="1" />
                                                                        <circle cx="5" cy="12" r="1" />
                                                                    </svg>
                                                                </button>
                                                                {openDropdownId === item.id && (
                                                                    <div
                                                                        className={styles.dropdownMenu}
                                                                        ref={actionMenuRef}
                                                                    >
                                                                        {hasCloseFinance && (
                                                                            <button
                                                                                className={styles.dropdownItem}
                                                                                onClick={(e) => handleDropdownItemClick(e, 'closeUnpaid', item.id)}
                                                                                disabled={actionLoadingId === item.id}
                                                                                type="button"
                                                                            >
                                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                    <path d="M9 12l2 2 4-4" />
                                                                                </svg>
                                                                                {actionLoadingId === item.id ? 'Closing...' : 'Close'}
                                                                            </button>
                                                                        )}
                                                                        {hasDeleteFinance && (
                                                                            <button
                                                                                className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                                                                onClick={(e) => handleDropdownItemClick(e, 'deleteUnpaid', item.id)}
                                                                                disabled={actionLoadingId === item.id}
                                                                                type="button"
                                                                            >
                                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                    <polyline points="3,6 5,6 21,6" />
                                                                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                                                </svg>
                                                                                {actionLoadingId === item.id ? 'Deleting...' : 'Delete'}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Show placeholder when no permissions */}
                                                        {((selectedTab.includes('-paid') && !hasDeleteFinance) ||
                                                            (selectedTab.includes('-unpaid') && !hasCloseFinance && !hasDeleteFinance) ||
                                                            ((selectedTab === 'finance' || selectedTab === 'maintenance' || selectedTab === 'utility') && !hasCloseFinance && !hasDeleteFinance)) && (
                                                                <span className={styles.noActionsText}>-</span>
                                                            )}
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {totalPages > 1 && (
                        <PageNavigator
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            ) : (
                <div className={styles.noDataMessage}>
                    <p>No {getStatusText()} {
                        selectedTab.includes('finance') ? 'Installments' :
                        selectedTab.includes('maintenance') ? 'Maintenance' : 'Utility'
                    } found.</p>
                </div>
            )}

            {/* Filter Dialog */}
            {isFilterDialogOpen && (
                <FilterDialog
                    isOpen={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleApplyFilter}
                    title="Filter Finance Records"
                    fields={filterFields}
                    initialValues={filters}
                />
            )}
        </div>
    );
};

export default FinanceTabContent;